import { useState, useEffect } from 'react';
import { SeoEntry, SeoFilterType, useSeoData, useSeoDelete } from '../../hooks/useSeoOperations';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import SeoEditModal from '../modals/SeoEditModal';

const FILTER_TABS: { key: SeoFilterType; label: string; icon: string }[] = [
    { key: 'all',      label: 'All Pages',    icon: '🌐' },
    { key: 'product',  label: 'Products',     icon: '🛒' },
    { key: 'category', label: 'Categories',   icon: '📂' },
    { key: 'brand',    label: 'Brands',       icon: '🏷️' },
    { key: 'seo',      label: 'Static Pages', icon: '📄' },
];

const TYPE_BADGE: Record<string, { label: string; color: string }> = {
    product:  { label: 'Product',      color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
    category: { label: 'Category',     color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
    brand:    { label: 'Brand',        color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
    seo:      { label: 'Static Page',  color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
};

export default function MarketingSeoTable() {
    const { seos, loading, error, fetchSeos } = useSeoData();
    const { deleteSeo, loading: deleteLoading } = useSeoDelete();

    const [activeFilter, setActiveFilter] = useState<SeoFilterType>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState<SeoEntry | null>(null);

    useEffect(() => {
        fetchSeos(activeFilter);
    }, [activeFilter]);

    const handleFilterChange = (filter: SeoFilterType) => {
        setActiveFilter(filter);
        setSearchTerm('');
    };

    const handleOpenModal = (entry?: SeoEntry) => {
        setSelectedEntry(entry || null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedEntry(null);
    };

    const handleSuccess = () => {
        fetchSeos(activeFilter);
        handleCloseModal();
    };

    const handleDelete = async (seoId: number) => {
        if (!window.confirm('Are you sure you want to delete this SEO record?')) return;
        const ok = await deleteSeo(seoId);
        if (ok) fetchSeos(activeFilter);
    };

    const filteredSeos = seos.filter(s =>
        s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.url?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.meta_title?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const configured   = seos.filter(s => s.seo_id !== null).length;
    const unconfigured = seos.filter(s => s.seo_id === null).length;

    return (
        <div className="space-y-6">
            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4">
                {[
                    { label: 'Total Pages',    value: seos.length,  color: 'text-gray-800 dark:text-white' },
                    { label: 'SEO Configured', value: configured,   color: 'text-green-600 dark:text-green-400' },
                    { label: 'Missing SEO',    value: unconfigured, color: 'text-red-500 dark:text-red-400' },
                ].map(stat => (
                    <div key={stat.label} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
                        <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
                        <p className={`text-3xl font-bold mt-1 ${stat.color}`}>{loading ? '—' : stat.value}</p>
                    </div>
                ))}
            </div>

            <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center flex-wrap gap-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-white">SEO Management</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage meta tags for all indexable pages</p>
                    </div>
                    <button
                        onClick={() => handleOpenModal()}
                        className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors flex items-center gap-2 text-sm font-medium"
                    >
                        <span className="text-base leading-none">+</span>
                        Add SEO Record
                    </button>
                </div>

                {/* Filter Tabs */}
                <div className="px-4 pt-3 pb-0 border-b border-gray-200 dark:border-gray-800 flex gap-1 overflow-x-auto">
                    {FILTER_TABS.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => handleFilterChange(tab.key)}
                            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-t-lg whitespace-nowrap border-b-2 transition-colors ${
                                activeFilter === tab.key
                                    ? 'border-brand-500 text-brand-600 dark:text-brand-400 bg-brand-50/50 dark:bg-brand-900/20'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            <span>{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Search */}
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
                    <input
                        type="text"
                        placeholder="Search by name, URL, or meta title…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-white text-sm"
                    />
                </div>

                {error && (
                    <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-red-700 dark:text-red-400 text-sm">
                        {error}
                    </div>
                )}

                {/* Table */}
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableCell isHeader className="px-5 py-3 text-start w-28">Type</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-start">Name / URL</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-start">Meta Title</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-start">Meta Description</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-center w-28">SEO Status</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-end w-24">Actions</TableCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="px-5 py-10 text-center">
                                        <div className="flex justify-center">
                                            <div className="w-6 h-6 border-2 border-brand-500/30 border-t-brand-500 rounded-full animate-spin" />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : filteredSeos.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
                                        {searchTerm ? 'No pages match your search.' : 'No pages found for this filter.'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredSeos.map(entry => {
                                    const badge = TYPE_BADGE[entry.entity_type] ?? { label: entry.entity_type, color: 'bg-gray-100 text-gray-600' };
                                    const hasSeo = entry.seo_id !== null;
                                    return (
                                        <TableRow key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                                            {/* Type */}
                                            <TableCell className="px-5 py-4">
                                                <span className={`text-xs px-2 py-1 rounded-full font-medium ${badge.color}`}>
                                                    {badge.label}
                                                </span>
                                            </TableCell>

                                            {/* Name / URL */}
                                            <TableCell className="px-5 py-4">
                                                <p className="font-medium text-gray-800 dark:text-white text-sm truncate max-w-[200px]">
                                                    {entry.name}
                                                </p>
                                                <code className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[200px] block">
                                                    {entry.url}
                                                </code>
                                            </TableCell>

                                            {/* Meta Title */}
                                            <TableCell className="px-5 py-4">
                                                {hasSeo && entry.meta_title ? (
                                                    <>
                                                        <p className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[180px]">{entry.meta_title}</p>
                                                        <p className="text-xs text-gray-400">{entry.meta_title.length}/60</p>
                                                    </>
                                                ) : (
                                                    <span className="text-xs text-gray-400 italic">Not set</span>
                                                )}
                                            </TableCell>

                                            {/* Meta Description */}
                                            <TableCell className="px-5 py-4">
                                                {hasSeo && entry.meta_description ? (
                                                    <>
                                                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-[220px]">{entry.meta_description}</p>
                                                        <p className="text-xs text-gray-400">{entry.meta_description.length}/160</p>
                                                    </>
                                                ) : (
                                                    <span className="text-xs text-gray-400 italic">Not set</span>
                                                )}
                                            </TableCell>

                                            {/* SEO Status */}
                                            <TableCell className="px-5 py-4 text-center">
                                                {hasSeo ? (
                                                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 font-medium">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                                                        Configured
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 font-medium">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
                                                        Missing
                                                    </span>
                                                )}
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell className="px-4 py-3 text-end">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => handleOpenModal(entry)}
                                                        title={hasSeo ? 'Edit SEO' : 'Add SEO'}
                                                        className="text-gray-500 hover:text-brand-500 dark:text-gray-400 dark:hover:text-brand-400 transition-colors"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                        </svg>
                                                    </button>
                                                    {hasSeo && (
                                                        <button
                                                            onClick={() => handleDelete(entry.seo_id!)}
                                                            disabled={deleteLoading}
                                                            title="Delete SEO"
                                                            className="text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 transition-colors disabled:opacity-40"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Footer */}
                {!loading && filteredSeos.length > 0 && (
                    <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 text-sm text-gray-500 dark:text-gray-400">
                        Showing {filteredSeos.length} of {seos.length} pages
                    </div>
                )}
            </div>

            <SeoEditModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSuccess={handleSuccess}
                seo={selectedEntry}
            />
        </div>
    );
}
